// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useWidgetBadges from "@insite/client-framework/Common/Hooks/useWidgetBadges";
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { ProductContext, ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import {
    isWishListLineDiscontinued,
    isWishListLineRestricted,
} from "@insite/client-framework/Store/Data/WishListLines/WishListLinesSelectors";
import setWishListLineIsSelected from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/SetWishListLineIsSelected";
import updateQuantity from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/UpdateQuantity";
import updateUnitOfMeasure from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/UpdateUnitOfMeasure";
import updateWishListLineQuantities from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/UpdateWishListLineQuantities";
import translate from "@insite/client-framework/Translate";
import { ProductModel, WishListLineModel, WishListModel } from "@insite/client-framework/Types/ApiModels";
import LocalizedDateTime from "@insite/content-library/Components/LocalizedDateTime";
import { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import ProductImageBadges, { ProductImageBadgesStyles } from "@insite/content-library/Components/ProductImageBadges";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import ProductQuantityBreakPricing, {
    ProductQuantityBreakPricingStyles,
} from "@insite/content-library/Components/ProductQuantityBreakPricing";
import ProductTextBadges, { ProductTextBadgesStyles } from "@insite/content-library/Components/ProductTextBadges";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox, { CheckboxPresentationProps, CheckboxProps } from "@insite/mobius/Checkbox";
import Clickable from "@insite/mobius/Clickable";
import GridContainer, { GridContainerProps, GridOffset } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Check from "@insite/mobius/Icons/Check";
import RefreshCw from "@insite/mobius/Icons/RefreshCw";
import X from "@insite/mobius/Icons/X";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import OverflowMenu, { OverflowMenuProps } from "@insite/mobius/OverflowMenu";
import { SelectPresentationProps } from "@insite/mobius/Select";
import { TextFieldProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CartLineContainer, {
    CartLineContainerProps,
    cartlineContainerStyles,
} from "@pacific/App/Components/Cart/CartLineContainer";
import xUseWidgetBadges from "@pacific/App/Hooks/xUseWidgetBadges";
import cartPageStyles from "@pacific/App/SharedStyle/CartPageStyles";
import productDescriptionVariantStyles from "@pacific/App/SharedStyle/ProductDescriptionStyles";
import ProductAddToCartButton from "@pacific/Overrides/Components/ProductAddToCartButton";
import ProductContextAvailability from "@pacific/Overrides/Components/ProductContextAvailability";
import ProductQuantityOrdered from "@pacific/Overrides/Components/ProductQuantityOrdered";
import ProductUnitOfMeasureSelect from "@pacific/Overrides/Components/ProductUnitOfMeasureSelect";
import { ellipsis, mergeWithCss, xScreen } from "@pacific/XDL/Utilities";
import { createLinkIconStyles } from "@pacific/XDL/Utilities/sharedStyles";
import cloneDeep from "lodash/cloneDeep";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    wishList: WishListModel;
    wishListLine: WishListLineModel;
    productInfo: ProductInfo;
    onDeleteClick: (wishListLine: WishListLineModel) => void;
    onEditNotesClick: (wishListLine: WishListLineModel) => void;
    showImageBadges: boolean;
    maximumNumberOfImageBadges: number;
    showTextBadges: boolean;
    maximumNumberOfTextBadges: number;
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => ({
    isSelected: !!state.pages.myListDetails.selectedWishListLineIds.find(o => o === ownProps.wishListLine.id),
    settingsCollection: getSettingsCollection(state),
    isQuantityUpdated: state.pages.myListDetails.wishListLinesWithUpdatedQuantity[ownProps.wishListLine.id],
});

const mapDispatchToProps = {
    setWishListLineIsSelected,
    updateUnitOfMeasure,
    updateQuantity,
    updateWishListLineQuantities,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface MyListsDetailsProductListLineStyles {
    editNotesLink?: LinkPresentationProps;
    lineInnerContainer?: CartLineContainerProps;
    selectedCheckboxHidden?: HiddenProps;
    imageGridItem?: GridItemProps;
    imageCheckboxWrapper?: InjectableCss;
    selectedCheckbox?: CheckboxPresentationProps;
    imageWrapper?: InjectableCss;
    editNotesHidden?: HiddenProps;
    productImageStyles?: ProductImageStyles;
    descriptionGridItem?: GridItemProps;
    productBrandStyles?: ProductBrandStyles;
    productDescriptionStyles?: ProductDescriptionStyles;
    productPartNumbersStyles?: ProductPartNumbersStyles;
    notesStyles?: SmallHeadingAndTextStyles;
    overflowMenuGridItem?: GridItemProps;
    mobileOverflowMenu?: OverflowMenuProps;
    partNumbersWrapper?: InjectableCss;
    partNumbersGridItem?: GridItemProps;
    priceAndAvailabilityGridItem?: GridItemProps;
    priceAndAvailabilityInnerContainer?: GridContainerProps;
    purchasedDateGridItem?: GridItemProps;
    purchasedDateLabelText?: TypographyPresentationProps;
    purchasedDateText?: TypographyPresentationProps;
    priceGridItem?: GridItemProps;
    productPricePrintLabel?: InjectableCss;
    productPriceStyles?: ProductPriceStyles;
    quantityBreakPricing?: ProductQuantityBreakPricingStyles;
    uomAndQuantityGridItem?: GridItemProps;
    uomAndQuantityInnerContainer?: GridContainerProps;
    uomGridItem?: GridItemProps;
    productUnitOfMeasureSelectStyles?: SelectPresentationProps;
    quantityGridItem?: GridItemProps;
    productQuantityOrderedStyles?: TextFieldProps;
    availabilityGridItem?: GridItemProps;
    productAvailabilityMessageStyles?: ProductAvailabilityStyles;
    productAvailabilityLinkStyles?: ProductAvailabilityStyles;
    notesHidden?: InjectableCss;
    notesGridItem?: GridItemProps;
    buttonsGridItem?: GridItemProps;
    addToCartButton?: ButtonPresentationProps;
    deleteLinkWrapper?: InjectableCss;
    actionsWrapper?: InjectableCss;
    deleteLink?: LinkPresentationProps;
    updateQuantityWrapper?: InjectableCss;
    updateQuantityLink?: LinkPresentationProps;
    quantityUpdatedWrapper?: InjectableCss;
    quantityUpdatedIcon?: IconPresentationProps;
    quantityUpdatedText?: TypographyPresentationProps;
    restrictedDescriptionGridItem?: GridItemProps;
    restrictedDescriptionText?: TypographyPresentationProps;
    restrictedMessageText?: TypographyPresentationProps;
    restrictedRemoveItemLink?: LinkPresentationProps;
    textBadgeStyles?: ProductTextBadgesStyles;
    imageBadgeStyles?: ProductImageBadgesStyles;
    partNumbersHidden?: HiddenProps;
    // XNG-Change
    lineContainer?: InjectableCss;
}

export const productListLineStyles: MyListsDetailsProductListLineStyles = {
    restrictedDescriptionText: {
        ...mergeWithCss(productDescriptionVariantStyles?.medium?.productDetailDescription, {
            css: css`
                ${ellipsis(2)}
            `,
        }),
    },
    editNotesLink: {
        typographyProps: { size: 12 },
        css: css`
            display: block;
            margin-top: 0px;
            line-height: 12px;
        `,
    },
    lineInnerContainer: {
        extendedStyles: {
            priceSection: {
                css: css`
                    ${xScreen("lte", "xs")} {
                        flex-wrap: wrap;
                    }
                `,
            },
            qty: {
                css: css`
                    ${xScreen("lte", "xs")} {
                        max-width: 75px;
                        margin-bottom: 10px;
                    }
                `,
            },
            container: {
                css: css`
                    position: relative;
                `,
            },
            notesFieldSection: {
                css: css`
                    gap: 6px;
                `,
            },
            remove: {
                css: css`
                    width: 100%;
                    justify-content: flex-end;
                `,
            },
            image: {
                css: css`
                    overflow: hidden;
                    img {
                        max-height: 88px;
                        object-fit: contain;
                    }
                `,
            },
            leftSide: {
                css: css`
                    ${cartlineContainerStyles?.leftSide?.css}
                    ${xScreen("lte", "xs")} {
                        max-width: calc(100% - 40px);
                    }
                `,
            },
        },
    },
    selectedCheckboxHidden: {
        below: "md",
        css: css`
            position: absolute;
            top: 1px;
            left: 5px;
        `,
    },
    imageGridItem: {
        width: [3, 3, 2, 2, 1],
        printWidth: 1,
    },
    imageCheckboxWrapper: {
        css: css`
            display: flex;
            flex-direction: row;
            flex-wrap: nowrap;
            position: relative;
        `,
    },
    selectedCheckbox: {
        sizeVariant: "small",
        css: css`
            @media print {
                display: none;
            }
            margin-right: 10px;
        `,
    },
    imageWrapper: {
        css: css`
            @media print {
                max-height: 83px;
                max-width: 83px;
            }
            width: 100%;
            min-width: 0;
            flex-direction: column;
        `,
    },
    editNotesHidden: { below: "md" },
    descriptionGridItem: {
        width: [8, 8, 4, 4, 4],
        printWidth: 6,
        css: css`
            flex-direction: column;
        `,
    },
    productPartNumbersStyles: {},
    mobileOverflowMenu: {
        cssOverrides: {
            wrapper: css`
                && {
                    position: absolute;
                    top: 10px;
                    right: 0;
                    ${xScreen("lte", "xs")} {
                        top: 5px;
                    }
                }
                ${xScreen("gte", "md")} {
                    display: none;
                }
            `,
        },
    },
    partNumbersWrapper: {
        css: css`
            @media print {
                display: block;
            }
            width: 100%;
        `,
    },
    partNumbersGridItem: {
        width: 12,
        css: css`
            padding: 0;
        `,
    },
    priceAndAvailabilityGridItem: {
        printWidth: 12,
        css: css`
            &&& {
                display: flex;
                flex-basis: auto;
                flex-grow: 1;
                max-width: none;
                justify-content: space-between;

                ${xScreen("lte", "md")} {
                    widthh: 100%;
                }
            }
        `,
    },
    priceAndAvailabilityInnerContainer: {
        gap: 10,
        css: css`
            height: 100%;
            > ${GridOffset} {
                height: 100%;
            }
        `,
    },
    purchasedDateGridItem: {
        width: [12, 12, 12, 12, 5],
        printWidth: 5,
        css: css`
            flex-direction: column;
        `,
    },
    purchasedDateLabelText: {
        variant: "p",
        transform: "uppercase",
        weight: 600,
        css: css`
            @media print {
                font-size: 12px;
            }
        `,
    },
    productPricePrintLabel: {
        css: css`
            display: none;
            @media print {
                display: block;
            }
        `,
    },
    priceGridItem: {
        width: 12,
        printWidth: 12,
        css: css`
            flex-direction: column;
            @media print {
                min-width: 150px;
            }
        `,
    },
    productPriceStyles: {
        priceWrapper: {
            css: css`
                display: flex;
                align-items: baseline;
                ${wrapInContainerStyles}
            `,
        },
        price: {
            priceText: {
                variant: "h6",
            },
            unitOfMeasure: {
                unitOfMeasureText: {
                    size: 12,
                    weight: 500,
                    css: css`
                        letter-spacing: 0;
                        line-height: 18px;
                    `,
                },
            },
        },
    },
    productDescriptionStyles: {
        ...mergeWithCss(cartPageStyles?.cartLine?.productDescription, {
            css: css`
                ${ellipsis(2)}
            `,
        }),
    },
    quantityBreakPricing: {
        viewLink: { typographyProps: { size: 12 } },
    },
    uomAndQuantityGridItem: {
        width: [12, 12, 7, 7, 7],
        printWidth: 7,
    },
    uomAndQuantityInnerContainer: {
        gap: 10,
        css: css`
            @media print {
                min-width: 150px;
            }
        `,
    },
    uomGridItem: { width: [12, 6, 6, 6, 6], printWidth: 6 },
    quantityGridItem: { width: [12, 3, 3, 3, 3], printWidth: 6 },
    productQuantityOrderedStyles: {
        labelProps: {
            css: css`
                @media print {
                    font-size: 12px;
                }
            `,
        },
        cssOverrides: {
            inputSelect: css`
                max-width: 75px;
            `,
        },
    },
    productUnitOfMeasureSelectStyles: {
        labelProps: {
            css: css`
                @media print {
                    font-size: 12px;
                }
            `,
        },
        cssOverrides: {
            formField: css`
                @media print {
                    display: none;
                }
            `,
        },
    },
    availabilityGridItem: {
        width: 12,
        css: css`
            align-items: end;
        `,
    },
    productAvailabilityLinkStyles: {
        container: {
            css: css`
                width: max-content;
                ${xScreen("lte", "xs")} {
                    width: 100%;
                }
            `,
        },
        availabilityByWarehouseLink: {
            typographyProps: {
                css: css`
                    line-height: 16px;
                `,
            },
        },
    },
    buttonsGridItem: {
        printWidth: 0,
        css: css`
            width: auto;
            max-width: unset;
            flex-direction: column;
            align-items: flex-end;
            margin-left: 10px;
            ${xScreen("lt", "md")} {
                display: none;
            }
        `,
    },
    notesHidden: {
        css: css`
            @media print {
                display: none;
            }
            width: 100%;
        `,
    },
    notesGridItem: { width: 12 },
    addToCartButton: {
        css: css`
            float: right;
            min-width: 130px;
        `,
    },
    deleteLinkWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: flex-end;
            margin-top: 10px;
            ${xScreen("lte", "sm")} {
                display: none;
            }
        `,
    },
    actionsWrapper: {
        css: css`
            display: flex;
            justify-content: flex-end;
            gap: 0 15px;
        `,
    },
    deleteLink: {
        ...createLinkIconStyles(X, {
            typographyProps: { size: 12 },
            icon: {
                iconProps: {
                    size: 12,
                },
            },
        }),
    },
    updateQuantityWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: center;
        `,
    },
    updateQuantityLink: {
        ...createLinkIconStyles(RefreshCw, {
            typographyProps: { size: 12 },
            icon: {
                iconProps: {
                    size: 12,
                },
            },
            css: css`
                margin-top: 10px;
            `,
        }),
    },
    quantityUpdatedWrapper: {
        css: css`
            margin-top: 10px;
            display: flex;
            align-items: center;
        `,
    },
    quantityUpdatedIcon: {
        src: Check,
        size: 16,
        css: css`
            margin-right: 5px;
        `,
    },
    quantityUpdatedText: {
        size: 12,
    },
    restrictedMessageText: { color: "warning" },
    textBadgeStyles: {
        container: {
            css: css`
                width: 60%;
                margin-bottom: 5px;
            `,
        },
        badgeText: {
            ellipsis: true,
            css: css`
                display: block;
                width: 100%;
                padding: 0 10px;
                // max width to stop extreme overflow effecting the line
                max-width: 300px;
            `,
        },
    },
    imageBadgeStyles: {
        container: {
            css: css`
                width: 60%;
                margin-bottom: 5px;
            `,
        },
        imageItem: {
            css: css`
                margin: 5px 0;
            `,
        },
    },
    partNumbersHidden: {
        below: "md",
        css: css`
            @media print {
                display: block;
            }
            width: 100%;
        `,
    },
    lineContainer: {
        css: css`
            ${cartlineContainerStyles?.container?.css}
            padding-left: 20px;
            position: relative;
            flex-wrap: nowrap;
        `,
    },

    restrictedDescriptionGridItem: {
        printWidth: 12,
        css: css`
            ${cartlineContainerStyles?.leftSide?.css}
            padding-left: 88px;
            flex-direction: column;
            flex-basis: auto;
            ${xScreen("lte", "xs")} {
                max-width: calc(100% - 40px);
            }
            @media print {
                max-width: unset;
            }
        `,
    },
};

const styles = productListLineStyles;

const MyListsDetailsProductListLine: React.FC<Props> = ({
    wishList,
    wishListLine,
    productInfo,
    isSelected,
    settingsCollection,
    isQuantityUpdated,
    onDeleteClick,
    onEditNotesClick,
    updateWishListLineQuantities,
    setWishListLineIsSelected,
    updateUnitOfMeasure,
    updateQuantity,
    showImageBadges,
    showTextBadges,
    maximumNumberOfImageBadges,
    maximumNumberOfTextBadges,
}) => {
    const product = mapWishListLineToProduct(wishListLine);

    const selectChangeHandler: CheckboxProps["onChange"] = (_, value) => {
        setWishListLineIsSelected({ wishListLineId: wishListLine.id, isSelected: value });
    };

    const onUpdateQuantityClick = () => {
        updateWishListLineQuantities({ reloadWishListLines: true });
    };
    /* Start XNGAGE Changes */
    const hideLoader = () => {
        setIsLoading(false);
    };
    /* END XNGAGE Changes */
    const canEditWishList = (wishList.allowEdit || !wishList.isSharedList) && !wishList.isAutogenerated;

    if (isWishListLineRestricted(wishListLine, productInfo) || isWishListLineDiscontinued(wishListLine, productInfo)) {
        return (
            <GridContainer
                {...styles.lineContainer}
                data-test-selector={`${wishListLine.productId}_${wishListLine.selectedUnitOfMeasure}`}
            >
                <GridItem {...styles.restrictedDescriptionGridItem}>
                    <Typography {...styles.restrictedDescriptionText}>{wishListLine.shortDescription}</Typography>
                    <ProductPartNumbers
                        productNumber={product.productNumber}
                        customerProductNumber={product.customerProductNumber}
                        manufacturerItem={product.manufacturerItem}
                        extendedStyles={styles.productPartNumbersStyles}
                    />
                </GridItem>
                <GridItem {...styles.priceAndAvailabilityGridItem}>
                    <Typography {...styles.restrictedMessageText} data-test-selector="restrictedMessage">
                        {isWishListLineRestricted(wishListLine, productInfo) && (
                            <>{siteMessage("Lists_Item_Not_Displayed_Due_To_Restrictions")}</>
                        )}
                        {isWishListLineDiscontinued(wishListLine, productInfo) && (
                            <>{siteMessage("Lists_Item_Not_Displayed_Due_To_Discontinued")}</>
                        )}
                    </Typography>
                    <StyledWrapper {...styles.buttonsGridItem}>
                        {canEditWishList && (
                            <Link
                                {...styles.deleteLink}
                                onClick={() => onDeleteClick(wishListLine)}
                                data-test-selector="removeRestricted"
                                aria-label="Delete"
                            >
                                {translate("Delete")}
                            </Link>
                        )}
                    </StyledWrapper>
                </GridItem>
                <OverflowMenu position="end" {...styles.mobileOverflowMenu}>
                    <Clickable onClick={() => onDeleteClick(wishListLine)} aria-label="Delete">
                        {translate("Delete")}
                    </Clickable>
                </OverflowMenu>
            </GridContainer>
        );
    }

    const { productSettings } = settingsCollection;
    const productContext: ProductContextModel = {
        product,
        productInfo,
        onQtyOrderedChanged: qtyOrdered => {
            updateQuantity({ wishListLineId: wishListLine.id, qtyOrdered });
        },
        onUnitOfMeasureChanged: unitOfMeasure => {
            setIsLoading(true); /* XNGAGE Changes */
            updateUnitOfMeasure({
                wishListLineId: wishListLine.id,
                unitOfMeasure,
                /* Start XNGAGE Changes */
                onSuccess: hideLoader,
                onComplete: hideLoader,
                /* END XNGAGE Changes */
            });
        },
    };

    const { imageBadges, textBadges } = xUseWidgetBadges({ productContext, showTextBadges, showImageBadges });

    const [isLoading, setIsLoading] = React.useState(false); /* XNGAGE Changes */
    return (
        <LoadingOverlay loading={isLoading}>
            {/* XNGAGE Changes: add loading overlay */}
            <ProductContext.Provider value={productContext}>
                <CartLineContainer
                    {...styles.lineInnerContainer}
                    data-test-selector={`${wishListLine.productId}_${wishListLine.selectedUnitOfMeasure}`}
                >
                    <CartLineContainer.Image>
                        <StyledWrapper {...styles.imageCheckboxWrapper}>
                            <Hidden {...styles.selectedCheckboxHidden}>
                                <Checkbox
                                    {...styles.selectedCheckbox}
                                    checked={isSelected}
                                    onChange={selectChangeHandler}
                                    data-test-selector="myListDetails_selectRowCheckbox"
                                />
                            </Hidden>
                            <StyledWrapper {...styles.imageWrapper}>
                                <ProductImage product={productContext} extendedStyles={styles.productImageStyles} />
                            </StyledWrapper>
                        </StyledWrapper>
                    </CartLineContainer.Image>

                    <CartLineContainer.Description>
                        {product.brand && (
                            <ProductBrand brand={product.brand} extendedStyles={styles.productBrandStyles} />
                        )}
                        <ProductDescription product={productContext} extendedStyles={styles.productDescriptionStyles} />
                        <StyledWrapper {...styles.partNumbersWrapper}>
                            <ProductPartNumbers
                                productNumber={product.productNumber}
                                customerProductNumber={product.customerProductNumber}
                                manufacturerItem={product.manufacturerItem}
                                extendedStyles={styles.productPartNumbersStyles}
                            />
                        </StyledWrapper>
                        {textBadges && (
                            <ProductTextBadges
                                badgeList={textBadges}
                                maxBadges={maximumNumberOfTextBadges ?? 4}
                                extendedStyles={styles.textBadgeStyles}
                            />
                        )}
                        {imageBadges && (
                            <ProductImageBadges
                                badgeList={imageBadges}
                                maxBadges={maximumNumberOfImageBadges ?? 4}
                                extendedStyles={styles.imageBadgeStyles}
                                numberOfColumns={4}
                            />
                        )}
                        {canEditWishList && (
                            <OverflowMenu position="end" {...styles.mobileOverflowMenu}>
                                <Clickable
                                    onClick={() => onEditNotesClick(wishListLine)}
                                    aria-label={translate(`${wishListLine.notes ? "Edit" : "Add"} Notes`)}
                                >
                                    {translate(`${wishListLine.notes ? "Edit" : "Add"} Notes`)}
                                </Clickable>
                                <Clickable onClick={() => onDeleteClick(wishListLine)} aria-label="Delete">
                                    {translate("Delete")}
                                </Clickable>
                            </OverflowMenu>
                        )}
                    </CartLineContainer.Description>

                    {canEditWishList && (
                        <CartLineContainer.LineNotesLink>
                            <Hidden {...styles.editNotesHidden}>
                                <Link
                                    {...styles.editNotesLink}
                                    onClick={() => onEditNotesClick(wishListLine)}
                                    data-test-selector="myListDetails_addOrEditNote"
                                    aria-label={wishListLine.notes ? "Edit Notes" : "Add Notes"}
                                >
                                    {translate(`${wishListLine.notes ? "Edit" : "Add"} Notes`)}
                                </Link>
                            </Hidden>
                        </CartLineContainer.LineNotesLink>
                    )}

                    <CartLineContainer.Availability>
                        {productSettings.showInventoryAvailability && (
                            <ProductContextAvailability
                                extendedStyles={styles.productAvailabilityMessageStyles}
                                hideLink
                            />
                        )}
                    </CartLineContainer.Availability>

                    <CartLineContainer.AvailabilityLink>
                        {productSettings.showInventoryAvailability && (
                            <ProductContextAvailability
                                extendedStyles={styles.productAvailabilityLinkStyles}
                                linkOnly
                            />
                        )}
                    </CartLineContainer.AvailabilityLink>
                    {wishList.isAutogenerated && (
                        <GridItem {...styles.purchasedDateGridItem}>
                            <Typography {...styles.purchasedDateLabelText}>{translate("Purchased Date")}</Typography>
                            <Typography {...styles.purchasedDateText} data-test-selector="purchasedDate">
                                <LocalizedDateTime dateTime={wishListLine.createdOn} />
                            </Typography>
                        </GridItem>
                    )}
                    <CartLineContainer.Price>
                        <StyledWrapper {...styles.productPricePrintLabel}>{translate("Price")}</StyledWrapper>
                        <ProductPrice
                            product={productContext}
                            showSavings={true}
                            showSavingsAmount={productSettings.showSavingsAmount}
                            showSavingsPercent={productSettings.showSavingsPercent}
                            extendedStyles={styles.productPriceStyles}
                        />
                        <ProductQuantityBreakPricing extendedStyles={styles.quantityBreakPricing} />
                    </CartLineContainer.Price>
                    <CartLineContainer.Qty>
                        <GridContainer {...styles.uomAndQuantityInnerContainer}>
                            <GridItem {...styles.quantityGridItem}>
                                <ProductQuantityOrdered
                                    labelOverride=""
                                    printLabelOverride={translate("QTY")}
                                    extendedStyles={styles.productQuantityOrderedStyles}
                                />
                            </GridItem>
                            {product.unitOfMeasures && product.unitOfMeasures.length > 1 && (
                                <GridItem {...styles.uomGridItem}>
                                    <ProductUnitOfMeasureSelect
                                        labelOverride=""
                                        printLabelOverride={translate("U/M")}
                                        disabled={!canEditWishList}
                                        extendedStyles={styles.productUnitOfMeasureSelectStyles}
                                    />
                                </GridItem>
                            )}
                        </GridContainer>
                    </CartLineContainer.Qty>

                    <CartLineContainer.Total>
                        <ProductAddToCartButton
                            notes={wishListLine.notes}
                            extendedStyles={styles.addToCartButton}
                            data-test-selector="addToCart"
                        />
                    </CartLineContainer.Total>

                    <CartLineContainer.Remove>
                        {canEditWishList && (
                            <Hidden {...styles.actionsWrapper}>
                                {((productInfo.qtyOrdered > 0 && wishListLine.qtyOrdered !== productInfo.qtyOrdered) ||
                                    (isQuantityUpdated && wishListLine.qtyOrdered === productInfo.qtyOrdered)) && (
                                    <StyledWrapper {...styles.updateQuantityWrapper}>
                                        {productInfo.qtyOrdered > 0 &&
                                            wishListLine.qtyOrdered !== productInfo.qtyOrdered && (
                                                <Link
                                                    {...styles.updateQuantityLink}
                                                    onClick={() => onUpdateQuantityClick()}
                                                    aria-label="Update QTY"
                                                >
                                                    {translate("Update QTY")}
                                                </Link>
                                            )}
                                        {isQuantityUpdated && wishListLine.qtyOrdered === productInfo.qtyOrdered && (
                                            <StyledWrapper {...styles.quantityUpdatedWrapper}>
                                                <Icon {...styles.quantityUpdatedIcon} />
                                                <Typography {...styles.quantityUpdatedText}>
                                                    {translate("QTY updated")}
                                                </Typography>
                                            </StyledWrapper>
                                        )}
                                    </StyledWrapper>
                                )}
                                <StyledWrapper {...styles.deleteLinkWrapper}>
                                    <Link
                                        {...styles.deleteLink}
                                        onClick={() => onDeleteClick(wishListLine)}
                                        data-test-selector="deleteListItem"
                                        aria-label="Delete"
                                    >
                                        {translate("Delete")}
                                    </Link>
                                </StyledWrapper>
                            </Hidden>
                        )}
                    </CartLineContainer.Remove>

                    <CartLineContainer.LineNotes>
                        {wishListLine.notes && (
                            <StyledWrapper {...styles.notesHidden}>
                                <SmallHeadingAndText
                                    heading={translate("Notes")}
                                    text={wishListLine.notes}
                                    extendedStyles={styles.notesStyles}
                                />
                            </StyledWrapper>
                        )}
                    </CartLineContainer.LineNotes>
                </CartLineContainer>
            </ProductContext.Provider>
        </LoadingOverlay>
    );
};

export const mapWishListLineToProduct = (wishListLine: WishListLineModel) => {
    const product = cloneDeep(wishListLine) as any as ProductModel;
    product.id = wishListLine.productId;
    product.productNumber = wishListLine.erpNumber;
    product.productTitle = wishListLine.shortDescription;
    product.customerProductNumber = wishListLine.customerName;
    product.unitOfMeasures =
        wishListLine?.productUnitOfMeasures?.map(u => ({
            id: u.productUnitOfMeasureId,
            ...u,
        })) || null;
    return product;
};

export default connect(mapStateToProps, mapDispatchToProps)(MyListsDetailsProductListLine);
